import { CoinbaseProfileMenuResource } from '@cbhq/identity-api-hooks/profile-menu/types';
import { createWrappedPromise } from '@cbhq/identity-shared/utils';

import { RCIDChangeEvent } from './RCIDChangeEvent';
import { deleteRcidCookie, readRcidCookie, writeRcidCookie } from './rcidCookie';
import { ResourceStorage } from './ResourceStorage';

function logRCIDChangeEvent(resourceId: string) {
  window.dispatchEvent(new RCIDChangeEvent(resourceId));
}

function getInitialResourceId(): string | undefined {
  if (typeof window !== 'undefined') {
    const fromUrl = new URLSearchParams(window.location.search).get('rcid');
    if (fromUrl) return fromUrl;

    const fromCookie = readRcidCookie();
    if (fromCookie) return fromCookie;
  }

  return ResourceStorage.getActiveResource();
}

class ResourceContextStore {
  private activeResourceId = getInitialResourceId();

  private resourcesPromise = createWrappedPromise<CoinbaseProfileMenuResource[]>();

  private async isValidResourceId(resourceId: string) {
    const resourceList = await this.resourcesPromise.promise;

    return resourceList.some((resource) => resource.identityAccountId === resourceId);
  }

  public async initializeAsync(): Promise<{
    needsSelection: boolean;
    resources?: CoinbaseProfileMenuResource[];
  }> {
    const resourceList = await this.resourcesPromise.promise;

    function getValidResource(resourceId: string | null | undefined) {
      return resourceList.find((resource) => resource.identityAccountId === resourceId);
    }

    const params = new URLSearchParams(window.location.search);
    const paramResource = getValidResource(params.get('rcid'));
    const cookieResource = getValidResource(readRcidCookie());
    const storageResource = getValidResource(ResourceStorage.getActiveResource());

    const resolvedResource = paramResource ?? cookieResource ?? storageResource;

    if (resolvedResource) {
      await this.persistActiveResource(resolvedResource.identityAccountId);
      return { needsSelection: false };
    }

    // Clear any stale RCID that didn't match a valid resource
    this.activeResourceId = undefined;
    ResourceStorage.clearActiveResource();
    deleteRcidCookie();

    if (resourceList.length <= 1) {
      const resourceId = resourceList[0]?.identityAccountId;
      if (resourceId) {
        await this.persistActiveResource(resourceId);
      }
      return { needsSelection: false };
    }

    // Multiple resources, no prior selection — needs blocking pick
    return { needsSelection: true, resources: resourceList };
  }

  public getActiveResourceId() {
    return this.activeResourceId;
  }

  public async getResourceFromId(resourceId: string | undefined) {
    const resourceList = await this.resourcesPromise.promise;
    return resourceList.find((resource) => resource.identityAccountId === resourceId);
  }

  public setResources(resources: CoinbaseProfileMenuResource[] | undefined) {
    this.resourcesPromise.reset();
    this.resourcesPromise.resolve(resources ?? []);
  }

  public async persistActiveResource(resourceId: string | undefined) {
    if (resourceId && (await this.isValidResourceId(resourceId))) {
      this.activeResourceId = resourceId;
      ResourceStorage.setActiveResource(resourceId);
      logRCIDChangeEvent(resourceId);
      writeRcidCookie(resourceId);
      return true;
    }
    return false;
  }

  public async selectWithoutPersist(resourceId: string | undefined) {
    if (resourceId && (await this.isValidResourceId(resourceId))) {
      this.activeResourceId = resourceId;
      logRCIDChangeEvent(resourceId);
      return true;
    }
    return false;
  }

  public reset() {
    this.activeResourceId = undefined;
    ResourceStorage.clearActiveResource();
    deleteRcidCookie();
  }

  // INTERNAL_getResources is used for testing
  public INTERNAL_getResources() {
    return this.resourcesPromise.promise;
  }
}

// INTERNAL_resourceContextStore is used for testing
export const INTERNAL_resourceContextStore = ResourceContextStore;

// THIS ONE is the actual export
export const resourceContextStore = new ResourceContextStore();
